import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiShoppingCart,
  FiTruck,
} from "react-icons/fi";
import axiosInstance from "../../utils/AxiosConfig";
import DownloadInvoiceButton from "../Common/DownloadInvoiceButton";
import { showError } from "../../utils/Toast";
import { getOrderPricingSummary } from "../../utils/orderSummary";
import {
  DEFAULT_PRODUCT_IMAGE,
  getDisplayPrice,
  getDisplayPricing,
  getProductStockQuantity,
  isProductOutOfStock,
  withDefaultProductImage,
} from "../../utils/productPresentation";
import "../../Styles/MyOrders.css";

const BASE_STAGES = [
  ["placed", "Order Placed"],
  ["accepted", "Accepted"],
  ["processing", "Processing"],
  ["packed", "Packed"],
  ["ready_to_ship", "Ready to Ship"],
  ["shipment_created", "Shipment Created"],
];
const LATER_STAGES = [
  ["picked_up", "Picked Up"],
  ["dispatched", "Shipped"],
  ["shipped", "Shipped"],
  ["in_transit", "In Transit"],
  ["out_for_delivery", "Out for Delivery"],
  ["delivered", "Delivered"],
];
const TERMINAL_STAGES = new Set([
  "cancelled",
  "rejected",
  "returned",
  "refunded",
  "failed",
  "failed_delivery",
  "unavailable",
]);
const LABELS = Object.fromEntries([...BASE_STAGES, ...LATER_STAGES]);
const normalizeStatus = (value) => {
  const key = String(value || "placed")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return key === "order_placed"
    ? "placed"
    : key === "accept"
      ? "accepted"
      : key;
};
const labelStatus = (value) =>
  LABELS[normalizeStatus(value)] ||
  normalizeStatus(value)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
const dateTime = (value, includeTime = false) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? { hour: "2-digit", minute: "2-digit", hour12: true }
      : {}),
  }).format(date);
};
const money = (value) => `AED ${Number(value || 0).toFixed(2)}`;
const parseObject = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return {};
};
const variantText = (item) => {
  const snapshot = parseObject(item.product_snapshot || item.product);
  const attributes = parseObject(snapshot.attributes || item.attributes);
  const fields = [
    ["Variant", snapshot.variant ?? attributes.variant],
    ["Size", snapshot.size ?? attributes.size],
    ["Color", snapshot.color ?? attributes.color],
    ["Unit", snapshot.unit ?? attributes.unit],
    ["Storage", snapshot.storage ?? attributes.storage],
    ["Pack", snapshot.pack_size ?? attributes.pack_size],
  ];
  return fields
    .filter(
      ([, value]) =>
        ["string", "number"].includes(typeof value) && String(value).trim(),
    )
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ");
};
const orderItems = (order) =>
  (Array.isArray(order.items) ? order.items : []).map((item) => {
    const snapshot = parseObject(item.product_snapshot || item.product);
    return {
      id: item.item_id || item.id || item.product_id,
      productId: Number(item.product_id || snapshot.id || 0),
      name:
        item.product_name || item.product?.name || snapshot.name || "Product",
      image: withDefaultProductImage(
        item.product_image ||
          item.product?.image_url ||
          item.product?.image_urls?.[0] ||
          snapshot.image_url,
      ),
      variant: variantText(item),
      quantity: Number(item.quantity || 1),
    };
  });
const timelineFor = (order) => {
  const current = normalizeStatus(
    order.display_status ||
      order.current_status ||
      order.status ||
      order.order_status,
  );
  const currentStage = ["pending", "pending_payment"].includes(current)
    ? "placed"
    : current === "confirmed"
      ? "accepted"
      : current;
  const sharedShipment = shipmentGroups(order);
  const shipmentEvents =
    sharedShipment.length === 1 && Array.isArray(sharedShipment[0].events)
      ? sharedShipment[0].events.map((event) => ({
          key: event.status,
          label: labelStatus(event.status),
          timestamp: event.event_time || event.created_at,
          completed: true,
        }))
      : [];
  const events = [
    ...(Array.isArray(order.status_timeline) ? order.status_timeline : []),
    ...shipmentEvents,
  ];
  const eventByKey = new Map(
    events.map((event) => [normalizeStatus(event.key || event.status), event]),
  );
  const stageKeys = BASE_STAGES.map(([key]) => key);
  const laterKeys = LATER_STAGES.map(([key]) => key).filter(
    (key) => key === current || eventByKey.has(key),
  );
  const isOutOfSequence =
    TERMINAL_STAGES.has(current) ||
    ![...stageKeys, ...LATER_STAGES.map(([key]) => key)].includes(currentStage);
  const keys = isOutOfSequence
    ? [...stageKeys, ...LATER_STAGES.map(([key]) => key)]
        .filter((key) => key === "placed" || eventByKey.has(key))
        .concat(current)
    : [...stageKeys, ...laterKeys];
  if (!keys.includes(currentStage)) keys.push(currentStage);
  const currentIndex = keys.indexOf(currentStage);
  return keys.map((key, index) => {
    const event = eventByKey.get(key);
    const recorded = Boolean(event);
    const reached = index < currentIndex || (recorded && event.completed);
    return {
      key,
      label: event?.label || labelStatus(key),
      active: key === currentStage,
      completed: reached || key === currentStage,
      date: dateTime(
        event?.timestamp || (key === "placed" ? order.created_at : null),
        true,
      ),
    };
  });
};
const shipmentGroups = (detail) => {
  const shipments = Array.isArray(detail?.shipments) ? detail.shipments : [];
  const groups = new Map();
  shipments.forEach((shipment) => {
    const key = shipment.awb_number
      ? `awb:${shipment.awb_number}`
      : `id:${shipment.id}`;
    const existing = groups.get(key);
    if (
      !existing ||
      (shipment.events?.length || 0) > (existing.events?.length || 0)
    )
      groups.set(key, shipment);
  });
  return [...groups.values()];
};

export default function MyOrders({
  orders: rawOrders,
  loading,
  error,
  onRefresh,
  insertCartRequest,
  showIntro = true,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [detailById, setDetailById] = useState({});
  const [visibleCount, setVisibleCount] = useState(8);
  const [buyingId, setBuyingId] = useState(null);
  const didSetInitialExpanded = useRef(false);
  const orders = useMemo(
    () =>
      (Array.isArray(rawOrders) ? rawOrders : [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at) - new Date(a.created_at) ||
            Number(b.order_id) - Number(a.order_id),
        ),
    [rawOrders],
  );
  const statusOptions = useMemo(
    () => [
      ...new Set(
        orders.map((order) =>
          normalizeStatus(order.display_status || order.status),
        ),
      ),
    ],
    [orders],
  );
  const filtered = useMemo(
    () =>
      orders.filter((order) => {
        const status = normalizeStatus(order.display_status || order.status);
        const needle = search.trim().toLowerCase();
        return (
          (filter === "all" || status === filter) &&
          (!needle ||
            [
              order.order_number,
              order.order_id,
              ...orderItems(order).map((item) => item.name),
            ].some((value) =>
              String(value || "")
                .toLowerCase()
                .includes(needle),
            ))
        );
      }),
    [orders, search, filter],
  );
  const visibleOrders = filtered.slice(0, visibleCount);

  useEffect(() => {
    if (didSetInitialExpanded.current || !orders.length) return;
    didSetInitialExpanded.current = true;
    const firstMulti = orders.find((order) => orderItems(order).length > 1);
    if (firstMulti) setExpanded(firstMulti.order_id);
  }, [orders]);
  useEffect(() => {
    setVisibleCount(8);
  }, [search, filter]);
  useEffect(() => {
    if (!expanded || detailById[expanded]) return;
    setDetailById((previous) => ({
      ...previous,
      [expanded]: { loading: true },
    }));
    axiosInstance
      .get(`orders/${expanded}`)
      .then((response) => {
        if (!response.data?.success || !response.data?.data)
          throw new Error("Order details unavailable");
        setDetailById((previous) => ({
          ...previous,
          [expanded]: { data: response.data?.data },
        }));
      })
      .catch(() => {
        setDetailById((previous) => ({
          ...previous,
          [expanded]: { error: "Tracking details could not be loaded." },
        }));
      });
  }, [expanded, detailById]);

  const buyAgain = async (order) => {
    const items = orderItems(order);
    const ids = [
      ...new Set(items.map((item) => item.productId).filter(Boolean)),
    ];
    if (!ids.length || buyingId) return;
    setBuyingId(order.order_id);
    try {
      const chunks = [];
      for (let i = 0; i < ids.length; i += 8) chunks.push(ids.slice(i, i + 8));
      const responses = await Promise.all(
        chunks.map((chunk) =>
          axiosInstance.get(`getProductById/${chunk.join(",")}`),
        ),
      );
      const currentProducts = new Map(
        responses
          .flatMap((response) => response.data?.data || [])
          .map((product) => [Number(product.id), product]),
      );
      let unavailable = 0;
      items.forEach((item) => {
        const product = currentProducts.get(item.productId);
        if (
          !product ||
          isProductOutOfStock(product) ||
          getProductStockQuantity(product) < item.quantity
        ) {
          unavailable += 1;
          return;
        }
        const pricing = getDisplayPricing(product);
        insertCartRequest({
          product_id: product.id,
          quantity: item.quantity,
          price: Number(getDisplayPrice(product)),
          discount: Number(product.discount || 0),
          attributes: product.attributes,
          ...(pricing.hasOffer
            ? {
                offer_id: pricing.offerId,
                original_price: pricing.originalPrice,
                offer_price: pricing.finalPrice,
                offer_discount_amount: pricing.discountAmount,
              }
            : {}),
        });
      });
      if (unavailable)
        showError(
          `${unavailable} product${unavailable === 1 ? " is" : "s are"} unavailable or have insufficient stock.`,
        );
    } catch {
      showError("Unable to check current products. Please try again.");
    } finally {
      setBuyingId(null);
    }
  };

  return (
    <div className="my-orders">
      {showIntro && <><div className="my-orders-breadcrumb">
        <button type="button" onClick={() => navigate("/")}>
          Home
        </button>
        <span>›</span>
        <span>My Account</span>
        <span>›</span>
        <strong>My Orders</strong>
      </div>
      <header className="my-orders-heading">
        <div>
          <h1>My Orders</h1>
          <p>Track, manage and reorder your medical supplies.</p>
        </div>
        <button
          type="button"
          className="my-orders-refresh"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh orders"
          title="Refresh orders"
        >
          <FiRefreshCw />
        </button>
      </header></>}
      <div className="my-orders-controls">
        <label className="my-orders-search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by order number or product name..."
            aria-label="Search orders"
          />
        </label>
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="Filter orders by status"
        >
          <option value="all">All Status</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {labelStatus(status)}
            </option>
          ))}
        </select>
      </div>
      {loading && !orders.length ? (
        <div className="my-orders-state" role="status">
          Loading your orders...
        </div>
      ) : error && !orders.length ? (
        <div className="my-orders-state" role="alert">
          <strong>Unable to load orders</strong>
          <p>{error}</p>
          <button type="button" onClick={onRefresh}>
            Try again
          </button>
        </div>
      ) : !orders.length ? (
        <div className="my-orders-state">
          <FiPackage />
          <strong>No orders yet</strong>
          <p>Your orders will appear here after checkout.</p>
        </div>
      ) : !filtered.length ? (
        <div className="my-orders-state">
          <FiSearch />
          <strong>No matching orders</strong>
          <p>Try another search or status.</p>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setFilter("all");
            }}
          >
            Show all orders
          </button>
        </div>
      ) : (
        <>
          <div className="my-orders-list">
            {visibleOrders.map((order) => {
              const id = order.order_id;
              const items = orderItems(order);
              const distinctProducts =
                new Set(items.map((item) => item.productId || item.id)).size ===
                items.length;
              const isOpen = expanded === id;
              const status = normalizeStatus(
                order.display_status || order.status,
              );
              const timeline = timelineFor(detailById[id]?.data || order);
              const latest =
                [...timeline].reverse().find((step) => step.completed) ||
                timeline[0];
              const detail = detailById[id]?.data;
              const shipments = shipmentGroups(detail);
              const deliveryTime =
                order.delivered_at ||
                detail?.delivered_at ||
                shipments
                  .flatMap((shipment) => shipment.events || [])
                  .find(
                    (event) => normalizeStatus(event.status) === "delivered",
                  )?.event_time;
              const trackingIds = shipments
                .map((shipment) => shipment.awb_number)
                .filter(Boolean);
              if (
                !trackingIds.length &&
                (detail?.courier_tracking_number ||
                  order.courier_tracking_number)
              )
                trackingIds.push(
                  detail?.courier_tracking_number ||
                    order.courier_tracking_number,
                );
              const total = getOrderPricingSummary(order).total;
              return (
                <article
                  className={`my-order-card${isOpen ? " is-open" : ""}`}
                  key={id}
                >
                  <div className="my-order-top">
                    <div className="my-order-ident">
                      <strong>#{order.order_number || id}</strong>
                      <span>Placed on {dateTime(order.created_at)}</span>
                    </div>
                    <div className="my-order-top-right">
                      <span className={`my-order-badge status-${status}`}>
                        {status === "delivered" ? (
                          <FiCheck />
                        ) : status === "shipment_created" ? (
                          <FiTruck />
                        ) : (
                          <FiClock />
                        )}
                        {labelStatus(status)}
                      </span>
                      <strong className="my-order-total">{money(total)}</strong>
                      <button
                        type="button"
                        className="my-order-chevron"
                        aria-label={`${isOpen ? "Collapse" : "Expand"} order ${order.order_number || id}`}
                        aria-expanded={isOpen}
                        aria-controls={`my-order-panel-${id}`}
                        onClick={() => setExpanded(isOpen ? null : id)}
                      >
                        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
                      </button>
                    </div>
                  </div>
                  {isOpen ? (
                    <div
                      id={`my-order-panel-${id}`}
                      className="my-order-expanded"
                    >
                      <div className="my-order-intro">
                        <strong>
                          {items.length}{" "}
                          {items.length === 1
                            ? "item"
                            : distinctProducts
                              ? "different items"
                              : "items"}
                        </strong>
                        <span> · </span>
                        <span>
                          {shipments.length === 1
                            ? "1 shipment"
                            : shipments.length > 1
                              ? `${shipments.length} shipments`
                              : labelStatus(status)}
                        </span>
                      </div>
                      <div className="my-order-products">
                        {items.map((item) => (
                          <div className="my-order-product" key={item.id}>
                            <img
                              src={item.image}
                              alt={item.name}
                              onError={(event) => {
                                event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                              }}
                            />
                            <div className="my-order-product-copy">
                              <strong>{item.name}</strong>
                              {item.variant && <span>{item.variant}</span>}
                              <small>Qty: {item.quantity}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                      {detailById[id]?.loading && (
                        <div className="my-order-detail-state" role="status">
                          Loading tracking details...
                        </div>
                      )}
                      {detailById[id]?.error && (
                        <div className="my-order-detail-state" role="alert">
                          {detailById[id].error}
                          <button
                            type="button"
                            onClick={() =>
                              setDetailById((previous) => {
                                const next = { ...previous };
                                delete next[id];
                                return next;
                              })
                            }
                          >
                            Retry
                          </button>
                        </div>
                      )}
                      {detail && (
                        <>
                          <div className="my-order-tracking-heading">
                            <strong>Order tracking</strong>
                            <span>
                              {latest?.label}
                              {latest?.date ? ` · ${latest.date}` : ""}
                            </span>
                          </div>
                          <ol className="my-order-timeline">
                            {timeline.map((stage) => (
                              <li
                                key={stage.key}
                                className={`${stage.completed ? "is-complete" : ""} ${stage.active ? "is-current" : ""}`}
                              >
                                <span className="my-order-node">
                                  {stage.completed ? <FiCheck /> : null}
                                </span>
                                <div>
                                  <strong>{stage.label}</strong>
                                  {stage.date && <small>{stage.date}</small>}
                                </div>
                              </li>
                            ))}
                          </ol>
                          {shipments.length > 1 ? (
                            <div className="my-order-shipments">
                              {shipments.map((shipment) => (
                                <div
                                  key={shipment.id}
                                  className="my-order-shipment"
                                >
                                  <div className="my-order-shipment-main">
                                    <FiTruck />
                                    <span>
                                      {shipment.delivery_partner_name ||
                                        "Shipment"}
                                      {shipment.awb_number
                                        ? ` · Tracking ID: ${shipment.awb_number}`
                                        : ""}
                                    </span>
                                    <strong>
                                      {labelStatus(
                                        shipment.shipment_status ||
                                          "shipment_created",
                                      )}
                                    </strong>
                                  </div>
                                  {Array.isArray(shipment.events) &&
                                    shipment.events.length > 0 && (
                                      <ul>
                                        {shipment.events.map((event) => (
                                          <li
                                            key={
                                              event.id ||
                                              `${event.status}-${event.event_time}`
                                            }
                                          >
                                            <span>
                                              {labelStatus(event.status)}
                                            </span>
                                            <time>
                                              {dateTime(
                                                event.event_time ||
                                                  event.created_at,
                                                true,
                                              )}
                                            </time>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                </div>
                              ))}
                            </div>
                          ) : trackingIds.length ? (
                            <div
                              className={`my-order-shipment-summary${status === "delivered" ? " is-delivered" : ""}`}
                            >
                              <FiTruck />
                              <strong>
                                {status === "delivered" &&
                                shipments.length === 1
                                  ? `${items.length === 1 ? "Item" : `All ${items.length} items`} delivered together`
                                  : "Shipment tracking"}
                              </strong>
                              {deliveryTime && (
                                <span>{dateTime(deliveryTime, true)}</span>
                              )}
                              <span>Tracking ID: {trackingIds[0]}</span>
                            </div>
                          ) : null}
                        </>
                      )}
                      <div className="my-order-actions">
                        <DownloadInvoiceButton
                          order={order}
                          className="my-order-button"
                        />
                        <button
                          type="button"
                          className="my-order-button"
                          onClick={() => navigate(`/account/orders/${id}`)}
                        >
                          View details
                        </button>
                        {status === "delivered" && (
                          <button
                            type="button"
                            className="my-order-button my-order-button-primary"
                            disabled={buyingId === id}
                            onClick={() => buyAgain(order)}
                          >
                            <FiShoppingCart />
                            {buyingId === id ? "Checking..." : "Buy again"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="my-order-preview">
                      <div className="my-order-preview-product">
                        <img
                          src={items[0]?.image || DEFAULT_PRODUCT_IMAGE}
                          alt={items[0]?.name || "Product"}
                          onError={(event) => {
                            event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                          }}
                        />
                        <div>
                          <strong>{items[0]?.name || "Product"}</strong>
                          {items[0]?.variant && <span>{items[0].variant}</span>}
                          <small>
                            Qty: {items[0]?.quantity || 1}
                            {items.length > 1
                              ? ` · +${items.length - 1} more ${items.length === 2 ? "item" : "items"}`
                              : ""}
                          </small>
                        </div>
                      </div>
                      <div className="my-order-latest">
                        <FiClock />
                        <div>
                          <strong>
                            {latest?.label || labelStatus(status)}
                          </strong>
                          {latest?.date && <small>{latest.date}</small>}
                        </div>
                      </div>
                      <div className="my-order-preview-actions">
                        <button
                          type="button"
                          className="my-order-button"
                          onClick={() => setExpanded(id)}
                        >
                          Track order
                        </button>
                        <button
                          type="button"
                          className="my-order-button"
                          onClick={() => navigate(`/account/orders/${id}`)}
                        >
                          View details
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          <div className="my-orders-footer">
            <span>
              Showing {visibleOrders.length} of {filtered.length} orders
            </span>
            {visibleCount < filtered.length && (
              <button
                type="button"
                className="my-order-button"
                onClick={() => setVisibleCount((count) => count + 8)}
              >
                Load more orders <FiChevronDown />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
